var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { consume } from '@lit/context';
import { LitElement, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { authenticationContext } from '../../../Authentication/context';
import { parseStripeProducts } from '../../../utils/parseStripeProducts';
import { formatCurrency } from '../../../utils/formatCurrency';
import { registerCustomElement } from '../../../utils/registerCustomElement';
import { styles } from './styles';
import config from '../../config';
let ModalUpgradePrompt = class ModalUpgradePrompt extends LitElement {
    constructor() {
        super(...arguments);
        // Parameters for customization
        this.promptDescription = '';
        // State to disable while redirecting
        this.chosenProduct = false;
        // Close/dismiss
        this._close = () => {
            this.dispatchEvent(new CustomEvent('close'));
        };
        // Choose product
        this._chooseProduct = (product) => {
            this.chosenProduct = true;
            this.dispatchEvent(new CustomEvent('chooseProduct', { detail: { product } }));
        };
        // Title
        // This changes if there's a trial eligible for our Pro products
        this._getPromptTitle = () => {
            var _a;
            // Get Pro products from Stripe
            const parsedProducts = parseStripeProducts(((_a = this.authentication) === null || _a === void 0 ? void 0 : _a.products) || []);
            // If they have a trial eligible in every product, show the trial title
            const hasTrialEligible = Object.values(parsedProducts).every(product => (product === null || product === void 0 ? void 0 : product.metadata.trial_eligible) === 'true');
            if (hasTrialEligible)
                return config.title.trial;
            // Otherwise, just return the regular title
            return config.title.default;
        };
        // Features
        // For now we're just showing "Pro", but in the future we'll show more tiers
        this._getFeatures = () => {
            var _a;
            // Get Pro product from Stripe
            const parsedProducts = parseStripeProducts(((_a = this.authentication) === null || _a === void 0 ? void 0 : _a.products) || []);
            const { proYearly } = parsedProducts;
            if (!proYearly)
                return '';
            // Parse features into list
            const features = (proYearly.metadata.features_ws || proYearly.metadata.features).split('|||');
            return html `
      ${features.map((f) => html `<div class="feature">
          <div class="icon">
            <grain-icon
              name="checkmark"
              width="24px"
              height="24px"
            ></grain-icon>
          </div>
          <div class="text">${f}</div>
        </div>`)}
      <div class="feature">
        <div class="icon">
          <grain-icon name="plus" width="24px" height="24px"></grain-icon>
        </div>
        <div class="text">Everything in Plus Membership</div>
      </div>
    `;
        };
        // Buttons
        // These change based on the user's current subscription period
        this._getButtons = () => {
            var _a, _b;
            // Get current sub from auth
            const currentSubscription = (_a = this.authentication) === null || _a === void 0 ? void 0 : _a.subscription;
            if (!currentSubscription)
                return html ``;
            // Grab products from Stripe
            const parsedProducts = parseStripeProducts(((_b = this.authentication) === null || _b === void 0 ? void 0 : _b.products) || []);
            const { plusMonthly, plusYearly, proMonthly, proYearly } = parsedProducts;
            if (!plusMonthly || !plusYearly || !proMonthly || !proYearly)
                return '';
            // Parse prices
            const proMonthlyPrice = formatCurrency(proMonthly.default_price.unit_amount / 100);
            const proYearlyPrice = formatCurrency(proYearly.default_price.unit_amount / 100);
            const proYearlyDiscountedPrice = proMonthly === null || proMonthly === void 0 ? void 0 : proMonthly.metadata.price_monthly;
            // Plus Monthly - Dismiss & Monthly
            if (currentSubscription.is_active &&
                currentSubscription.sku === plusMonthly.id) {
                return html `
        <grain-button
          size="lg"
          ?fullWidth=${true}
          variant="secondary"
          @click=${() => this._close()}
          ?disabled=${this.chosenProduct}
          >No thanks</grain-button
        >
        <grain-button
          size="lg"
          ?fullWidth=${true}
          @click=${() => this._chooseProduct(proMonthly)}
          ?disabled=${this.chosenProduct}
          >${proMonthlyPrice} ${config.buttons.monthly}*</grain-button
        >
      `;
            }
            // Plus Yearly - Dismiss & Yearly
            if (currentSubscription.is_active &&
                currentSubscription.sku === plusYearly.id) {
                return html `
        <grain-button
          size="lg"
          ?fullWidth=${true}
          variant="secondary"
          @click=${() => this._close()}
          ?disabled=${this.chosenProduct}
          >No thanks</grain-button
        >
        <grain-button
          size="lg"
          ?fullWidth=${true}
          @click=${() => this._chooseProduct(proYearly)}
          ?disabled=${this.chosenProduct}
          >${proYearlyPrice} ${config.buttons.yearly}*</grain-button
        >
      `;
            }
            // Default - Monthly & Yearly
            return html `
      <div>
        <grain-button
          size="lg"
          ?fullWidth=${true}
          variant="secondary"
          @click=${() => this._chooseProduct(proMonthly)}
          ?disabled=${this.chosenProduct}
          >${proMonthlyPrice} ${config.buttons.monthly}*</grain-button
        >
        <div class="claim">* ${config.disclaimers.monthly}</div>
      </div>
      <div>
        <grain-button
          size="lg"
          ?fullWidth=${true}
          @click=${() => this._chooseProduct(proYearly)}
          ?disabled=${this.chosenProduct}
          >${proYearlyDiscountedPrice} ${config.buttons.monthly}**</grain-button
        >
        <div class="claim">** ${config.disclaimers.yearly}</div>
      </div>
    `;
        };
        // Disclaimer
        // This changes based on the user's current subscription period
        this._getDisclaimer = () => {
            var _a, _b;
            // If subscribed already, they'll be prorated
            if ((_b = (_a = this.authentication) === null || _a === void 0 ? void 0 : _a.subscription) === null || _b === void 0 ? void 0 : _b.is_active) {
                return html `
        <p>* ${config.disclaimers.plus}</p>
        <p>** ${config.disclaimers.transactions}</p>
      `;
            }
            return html `<p>${config.disclaimers.transactions}</p>`;
        };
    }
    render() {
        return html `<div class="prompt">
      <!-- Title -->
      <h2 class="title">${this._getPromptTitle()}</h2>

      <!-- Description -->
      ${ifDefined(this.promptDescription)
            ? html `<p class="description">${this.promptDescription}</p>`
            : null}

      <!-- Features -->
      <div class="features">${this._getFeatures()}</div>

      <!-- Buttons -->
      <div class="buttons">${this._getButtons()}</div>

      <!-- Disclaimer -->
      <div class="disclaimer">${this._getDisclaimer()}</div>
    </div>`;
    }
};
ModalUpgradePrompt.styles = styles;
__decorate([
    consume({ context: authenticationContext, subscribe: true })
], ModalUpgradePrompt.prototype, "authentication", void 0);
__decorate([
    property({ type: String })
], ModalUpgradePrompt.prototype, "promptDescription", void 0);
__decorate([
    state()
], ModalUpgradePrompt.prototype, "chosenProduct", void 0);
ModalUpgradePrompt = __decorate([
    registerCustomElement('grain-modal-upgrade-prompt')
], ModalUpgradePrompt);
export { ModalUpgradePrompt };
